import { BrowserTracing } from "@sentry/tracing"
import type { Breadcrumb, Primitive, User } from "@sentry/types"
import * as Sentry from "@sentry/vue"

import { useUserStore } from "../stores/userStore"

export default defineNuxtPlugin((nuxtApp) => {
  const { vueApp } = nuxtApp
  const environment = nuxtApp.$config.public.appEnv
  const enabled = ["production", "staging"].includes(environment)

  // We dont want Sentry to be initialized in development env,
  // because it catches errors still and throws in console
  // different stack trace and error message
  if (enabled) {
    Sentry.init({
      app: vueApp,
      debug: false,
      dsn: "https://e5744fb53bb34d96bd9fddbecd13dcf2@o185386.ingest.sentry.io/4505040594731008",
      enabled,
      environment,
      integrations: [new BrowserTracing()],
      sampleRate: 0.2,
      maxBreadcrumbs: 50,
      tracesSampleRate: 0,
      ignoreErrors: [/ResizeObserver/i, /FetchError/i],
      trackComponents: false,
      beforeSend(event) {
        const userStore = useUserStore()
        if (userStore.current?.id) {
          event.user = {
            ...event.user,
            id: String(userStore.current.id),
          }
          event.tags = {
            ...event.tags,
            user_id: String(userStore.current.id),
          }
        }
        if (userStore.authToken) {
          event.tags = {
            ...event.tags,
            token_prefix: userStore.authToken.substring(0, 6),
          }
        }
        return event
      },
    })

    Sentry.attachErrorHandler(vueApp, {
      logErrors: false,
      attachProps: true,
      trackComponents: true,
      timeout: 2000,
      hooks: ["activate", "create", "mount", "update"],
    })

    return {
      provide: {
        sentrySetContext: (n: string, context: { [key: string]: any }) => Sentry.setContext(n, context),
        sentrySetUser: (user: User) => Sentry.setUser(user),
        sentrySetTag: (tagName: string, value: Primitive) => Sentry.setTag(tagName, value),
        sentryAddBreadcrumb: (breadcrumb: Breadcrumb) => Sentry.addBreadcrumb(breadcrumb),
      },
    }
  } else {
    return {}
  }
})
